import { Controller } from "@hotwired/stimulus"

// FD-500: shared option-list templates. Big option lists (487 workspace members,
// ~95 project labels) used to be inlined into EVERY spreadsheet row / sub-item
// row — O(rows × members) HTML that blew past Cloud Run's 32 MiB response cap on
// large epics. The list is now rendered ONCE per page as a <template>; this
// controller clones it into the row's dropdown/popover on first open, applies
// the row's selected state from a data attribute, and (optionally) points the
// cloned form at the row's own URL.
//
//   <div data-controller="clone-options"
//        data-clone-options-template-id-value="spreadsheet-assignees-tpl"
//        data-clone-options-selected-value="[1,2]"
//        data-clone-options-url-value="/acme/projects/1/items/42"
//        data-action="focusin->clone-options#populate">
//     ...trigger...
//     <div data-clone-options-target="slot"></div>
//   </div>
//
// The template may contain a whole <form> (spreadsheet dropdowns — urlValue
// rewrites its action) or bare option rows cloned into an existing form
// (popover_select's list — no urlValue needed).
export default class extends Controller {
  static targets = ["slot"]
  static values = { templateId: String, selected: Array, url: String }

  // FD-603: "have I cloned yet?" is recorded on the SLOT, not on the controller
  // instance. Picking a value auto-submits (the spreadsheet dropdowns only started
  // doing this once their CSP-dead inline onchange became a Stimulus action), which
  // redirect_back's into a morph — and the server renders the slot empty, so idiomorph
  // strips the cloned rows. The element itself survives, so the controller instance does
  // too: a boolean latch would stay `true` and every later open would show an empty
  // dropdown until a full reload. The marker attribute is removed by the same morph that
  // removes the rows, so the two can't disagree.
  populate() {
    if (this.slotTarget.dataset.cloneOptionsPopulated) return
    const template = document.getElementById(this.templateIdValue)
    if (!template) return
    this.slotTarget.dataset.cloneOptionsPopulated = "true"

    const fragment = template.content.cloneNode(true)
    const selected = new Set(this.selectedValue.map(String))
    fragment.querySelectorAll("option").forEach((option) => {
      if (selected.has(option.value)) option.selected = true
    })
    fragment.querySelectorAll("input[type=checkbox], input[type=radio]").forEach((input) => {
      if (selected.has(input.value)) input.checked = true
    })
    if (this.hasUrlValue && this.urlValue !== "") {
      fragment.querySelectorAll("form").forEach((form) => form.setAttribute("action", this.urlValue))
    }
    this.slotTarget.appendChild(fragment)
  }
}
